import { useState, useEffect, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialReactTable } from 'material-react-table';
import Typography from '@mui/material/Typography';
import historicalContentService from '../../services/historicalContent.service';
import styles from './historicalContent.module.css';

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	DialogContentText,
	IconButton,
	TextField,
	Tooltip,
	Paper,
	Chip,
	Snackbar,
	Alert,
	LinearProgress
} from '@mui/material';
const BASE_URL = process.env.REACT_APP_BASE_URL;

function HistoricalContent(props) {
	const [jobsData, setJobsData] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [openCreateJobDialog, setCreateJobOpenDialog] = useState(false);
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 10; // Using constant instead of state since it's not changing
	const [isLoading, setIsLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState('success');
	const [formData, setFormData] = useState({
		name: '',
		fromDate: '',
		toDate: '',
		query: '',
		dailyLimit: '20000',
	});

	const handleDownloadClick = (id) => {
		console.log('Download Click', id);
		setSelectedId(id);
		setOpenDialog(true);
	};
	
	const handleAddJobClick = () => {
		console.log('Add job Click');
		setCreateJobOpenDialog(true);
	};

	const handlePrevPage = () => {
		if (page > 1) {
			setPage(page - 1);
		}
	};

	const handleNextPage = () => {
		setPage(page + 1);
	};
	
	const handleDialogClose = () => {
		console.log({ message: 'in dialog close' });
		setOpenDialog(false);
		setSelectedId(null);
		fetchData();
	};

	const handleCreateJobDialogClose = () => {
		console.log({ message: 'in create job dialog close' });
		setCreateJobOpenDialog(false);
	};

	const handleConfirmDownload = () => {
		console.log({ message: 'in handleConfirmDownload', selectedId });
		if (selectedId !== null) {
			const fileUrl = `${BASE_URL}/hc/download-files?jobId=${selectedId}`;

			// Create a temporary link element
			const link = document.createElement('a');
			link.href = fileUrl;
			link.target = '_blank';
			link.download = 'historical-content.pdf';

			// Trigger a click event on the link to initiate the download
			link.click();
			
			setSnackbarMessage('File download started');
			setSnackbarSeverity('success');
			setOpenSnackbar(true);

			setOpenDialog(false);
			setSelectedId(null);
			fetchData();
		}
	};
	
	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar(false);
	};
	
	// Manual refresh button handler
	const handleManualRefresh = () => {
		setIsRefreshing(true);
		fetchData(true).then(() => {
			setIsRefreshing(false);
			setSnackbarMessage('Data has been refreshed');
			setSnackbarSeverity('success');
			setOpenSnackbar(true);
		});
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		console.log('Form Data:', formData);

		setIsLoading(true);
		historicalContentService
			.createJob(formData)
			.then((response) => {
				console.log({ message: 'Job created successfully', response });
				setFormData({
					name: '',
					fromDate: '',
					toDate: '',
					query: '',
					dailyLimit: '20000',
				});
				setCreateJobOpenDialog(false);
				setSnackbarMessage('Job created successfully! Watch the status column for updates.');
				setSnackbarSeverity('success');
				setOpenSnackbar(true);
				fetchData();
			})
			.catch((error) => {
				console.error('Error creating job:', error);
				setSnackbarMessage('Error creating job: ' + error.message);
				setSnackbarSeverity('error');
				setOpenSnackbar(true);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const fetchData = (showLoader = true) => {
		console.log({ message: 'In fetchData' });
		if (showLoader) {
			setIsLoading(true);
		}
		
		return historicalContentService
			.getJobs(page, PAGE_SIZE)
			.then((response) => {
				setJobsData(Array.isArray(response) ? response : []);
			})
			.catch((error) => {
				console.error('Error fetching dashboard data:', error);
				if (showLoader) {
					setSnackbarMessage('Error refreshing data: ' + error.message);
					setSnackbarSeverity('error');
					setOpenSnackbar(true);
				}
			})
			.finally(() => {
				if (showLoader) {
					setIsLoading(false);
				}
			});
	};
	
	useEffect(() => {
		// Make a GET request to fetch dashboard data
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	const columns = useMemo(
		() => [
			{
				header: 'Actions',
				size: 80,
				Cell: ({ row }) => (
					<div className={styles.actionsCell}>
						{row.original.status === 'completed' ? (
							<Tooltip arrow placement='right' title='Download'>
								<IconButton
									className={styles.downloadButton}
									onClick={() => handleDownloadClick(row.original.id)}
									size="small"
								>
									<DownloadIcon />
								</IconButton>
							</Tooltip>
						) : null}
					</div>
				),
			},
			{
				header: 'Id',
				accessorKey: 'id',
				size: 180,
			},
			{
				header: 'Name',
				accessorKey: 'name',
				size: 150,
			},
			{
				header: 'From Date',
				accessorKey: 'fromDate',
				size: 120,
			},
			{
				header: 'To Date',
				accessorKey: 'toDate',
				size: 120,
			},
			{
				header: 'Query',
				accessorKey: 'query',
				size: 200,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					// Truncate long query strings with ellipsis
					return value && value.length > 50 
						? <Tooltip title={value}><span>{value.substring(0, 50) + '...'}</span></Tooltip>
						: <span>{value || 'N/A'}</span>;
				},
			},
			{
				header: 'Status',
				accessorKey: 'status',
				size: 120,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					
					// Different styling for different status values
					let chipClassName;
					let progressBar = null;
					
					switch(value) {
						case 'processing':
							chipClassName = styles.statusProcessing;
							progressBar = <LinearProgress style={{ width: '60px', height: '4px' }} color="primary" />;
							break;
						case 'pending':
							chipClassName = styles.statusPending;
							progressBar = <LinearProgress style={{ width: '60px', height: '4px' }} color="secondary" />;
							break;
						case 'completed':
							chipClassName = styles.statusCompleted;
							break;
						default:
							chipClassName = styles.statusDefault;
					}
					
					return (
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Chip 
								label={value}
								variant="outlined"
								size="small"
								className={`${styles.statusChip} ${chipClassName}`}
							/>
							{(value === 'processing' || value === 'pending') && progressBar}
						</div>
					);
				},
			},
			{
				header: 'Estimated Count',
				accessorKey: 'count',
				size: 150,
				Cell: ({ cell }) => {
					const count = cell.getValue();
					return <span>{count !== undefined ? count.toLocaleString() : 'N/A'}</span>;
				},
			},
		],
		[]
	);
	
	// Render the pagination controls
	const renderPagination = () => {
		return (
			<div className={styles.paginationContainer}>
				<div className={styles.paginationLeft}>
					<Button 
						variant="contained"
						onClick={handlePrevPage}
						disabled={page === 1}
						className={styles.paginationButton}
					>
						Previous Page
					</Button>
					
					<Chip 
						label={`Page ${page}`} 
						variant="outlined"
						className={styles.pageIndicator}
					/>
				</div>
				
				<div className={styles.paginationRight}>
					<Button
						variant="outlined"
						onClick={handleManualRefresh}
						startIcon={<RefreshIcon className={isRefreshing ? styles.spinIcon : ''} />}
						disabled={isRefreshing}
						className={styles.refreshButton}
					>
						{isRefreshing ? 'Refreshing...' : 'Refresh'}
					</Button>
					
					<Button 
						variant="contained"
						onClick={handleNextPage}
						className={styles.paginationButton}
					>
						Next Page
					</Button>
				</div>
			</div>
		);
	};

	return (
		<div className={styles.container}>
			{/* Confirmation Dialog */}
			<Dialog 
				open={openDialog} 
				onClose={handleDialogClose}
				PaperProps={{
					style: { borderRadius: '8px' }
				}}
			>
				<DialogTitle className={styles.dialogTitle}>
					Confirm Download
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<DialogContentText>
						Are you sure you want to download this historical content file?
					</DialogContentText>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button 
						onClick={handleDialogClose} 
						className={styles.dialogCancelButton}
					>
						Cancel
					</Button>
					<Button 
						onClick={handleConfirmDownload} 
						variant="contained"
						className={styles.dialogSubmitButton}
					>
						Download
					</Button>
				</DialogActions>
			</Dialog>

			{/* Create Job Dialog */}
			<Dialog 
				open={openCreateJobDialog} 
				onClose={handleCreateJobDialogClose}
				fullWidth
				maxWidth='sm'
				PaperProps={{
					style: { borderRadius: '8px' }
				}}
			>
				<DialogTitle className={styles.dialogTitle}>
					Create Historical Content Job
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<form onSubmit={handleSubmit}>
						<Box className={styles.dialogFormContainer}>
							<div>
								<TextField
									label='Name'
									name='name'
									value={formData.name}
									onChange={handleChange}
									fullWidth
									required
									margin='normal'
								/>
								<TextField
									label='From Date'
									type='date'
									name='fromDate'
									value={formData.fromDate}
									onChange={handleChange}
									fullWidth
									required
									margin='normal'
									InputLabelProps={{
										shrink: true,
									}}
								/>
								<TextField
									label='To Date'
									type='date'
									name='toDate'
									value={formData.toDate}
									onChange={handleChange}
									fullWidth
									required
									margin='normal'
									InputLabelProps={{
										shrink: true,
									}}
								/>
								<TextField
									label='Query'
									name='query'
									value={formData.query}
									onChange={handleChange}
									fullWidth
									required
									multiline
									rows={4}
									margin='normal'
								/>
								<TextField
									label='Daily Limit'
									type='number'
									name='dailyLimit'
									value={formData.dailyLimit}
									onChange={handleChange}
									fullWidth
									required
									margin='normal'
								/>
							</div>
						</Box>
						<DialogActions className={styles.dialogActions}>
							<Button 
								onClick={handleCreateJobDialogClose}
								className={styles.dialogCancelButton}
							>
								Cancel
							</Button>
							<Button 
								type='submit' 
								variant='contained'
								className={styles.dialogSubmitButton}
								disabled={isLoading}
							>
								{isLoading ? 'Creating...' : 'Create Job'}
							</Button>
						</DialogActions>
					</form>
				</DialogContent>
			</Dialog>

			<div className={styles.headerContainer}>
				<div className={styles.headerRow}>
					<Button 
						className={styles.backButton}
						onClick={() => window.history.back()}
						startIcon={<ArrowBackIcon />}
					>
						Back
					</Button>
					<Typography variant='h6' color='black' className={styles.tableHeader}>
						Historical Content Jobs
					</Typography>
				</div>
				
				<Button
					variant='contained'
					onClick={handleAddJobClick}
					className={styles.createButton}
					startIcon={<AddIcon />}
				>
					Create a new Job
				</Button>
			</div>

			<Paper className={styles.tableContainer}>
				{renderPagination()}
				
				{isLoading && (
					<LinearProgress sx={{ mt: 1 }} />
				)}
				
				<div className={styles.tableWrapper}>
					<MaterialReactTable
						columns={columns}
						data={jobsData}
						enablePagination={false}
						enableColumnOrdering
						enableColumnResizing
						enableStickyHeader
						muiTableContainerProps={{ 
							className: styles.expandableTable
						}}
						enableGlobalFilter
						enableColumnFilters={false}
						muiTablePaperProps={{ elevation: 0 }}
						muiSearchTextFieldProps={{
							variant: 'outlined',
							size: 'small',
							placeholder: 'Search all jobs...',
							className: styles.searchField
						}}
						initialState={{ 
							density: 'compact',
							columnVisibility: { 
								id: true
							}
						}}
					/>
				</div>
			</Paper>
			
			{/* Success/Error notifications */}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert 
					onClose={handleCloseSnackbar} 
					severity={snackbarSeverity}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</div>
	);
}

HistoricalContent.propTypes = {};

HistoricalContent.defaultProps = {};

export default HistoricalContent;
